import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { SwatchBook } from "lucide-react";
import wordmark from "../assets/brand/wordmark_navy.png";
import { detectOrgSlug, fetchCobrand } from "../lib/api";
import AccountMenu from "./AccountMenu";

const GROUNDS = [
  { key: "ivory", label: "Ivory",   hint: "Warm ivory" },
  { key: "white", label: "White",   hint: "Studio white" },
  { key: "lilac", label: "Lilac",   hint: "Original wash" },
];

function readGround() {
  try {
    const v = localStorage.getItem("klareet.ground");
    if (v && GROUNDS.some((g) => g.key === v)) return v;
  } catch (_e) { /* ignore */ }
  return "ivory";
}

export default function KlareetHeader({ product }) {
  const location = useLocation();
  const [cobrand, setCobrand] = useState(null);
  const [ground, setGround] = useState(readGround());

  useEffect(() => {
    const slug = detectOrgSlug();
    if (slug) fetchCobrand(slug).then(setCobrand);
  }, []);

  // KLA-048: Klareet is light by design. Clear any dark class a
  // browser may still carry from before the dark theme was removed.
  useEffect(() => {
    document.documentElement.classList.remove("dark");
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-ground", ground);
    try { localStorage.setItem("klareet.ground", ground); } catch (_e) { /* ignore */ }
  }, [ground]);

  const cycleGround = () => {
    const i = GROUNDS.findIndex((g) => g.key === ground);
    setGround(GROUNDS[(i + 1) % GROUNDS.length].key);
  };

  const entryPath = ["/", "/consent", "/grow", "/grow/consent"].includes(location.pathname);
  const wordmarkClass = entryPath ? "h-10 sm:h-12" : "h-7";
  const currentGround = GROUNDS.find((g) => g.key === ground) || GROUNDS[0];
  const authed = typeof window !== "undefined" && !!localStorage.getItem("klareet.access");

  return (
    <header
      className="w-full border-b sticky top-0 z-30 backdrop-blur-md print:hidden"
      style={{ borderColor: "hsl(var(--klareet-line))", background: "hsl(var(--background) / 0.85)" }}
      data-testid="klareet-header"
    >
      <div className="w-full flex items-center justify-between px-4 sm:px-6 py-3">
        <Link
          to={product === "grow" ? "/grow" : "/"}
          className="flex items-center gap-3 group shrink-0"
          data-testid="klareet-logo"
        >
          <img
            src={wordmark}
            alt="Klareet"
            className={`${wordmarkClass} transition-transform duration-300 group-hover:scale-[1.03]`}
            data-testid="klareet-wordmark"
          />
          <span className="k-chip" data-testid="klareet-subproduct">
            {product === "grow" ? "Grow" : "Start"}
          </span>
          {cobrand && (
            <span
              className="flex items-center gap-2 pl-3 ml-1 border-l"
              style={{ borderColor: "hsl(var(--klareet-line))" }}
              data-testid="cobrand-block"
            >
              {cobrand.logo_url ? (
                <img src={cobrand.logo_url} alt="" className="h-5" aria-hidden="true" />
              ) : null}
              <span className="text-sm font-medium" data-testid="cobrand-name">
                {cobrand.display_name}
              </span>
            </span>
          )}
        </Link>
        <div className="flex items-center gap-2">
          {cobrand && (
            <span
              className="hidden md:inline text-xs mr-2"
              style={{ color: "hsl(var(--klareet-muted))" }}
              data-testid="cobrand-privacy"
            >
              {cobrand.privacy_line}
            </span>
          )}

          {/* Temporary ground toggle. Removed after the ground is
              decided. Cycles through ivory, white, and lilac. */}
          <button
            onClick={cycleGround}
            className="k-btn k-btn-ghost !p-2 !px-3 flex items-center gap-1.5"
            aria-label={`ground is ${currentGround.hint}, tap to change`}
            title={`${currentGround.hint} · tap to change`}
            data-testid="ground-toggle"
          >
            <SwatchBook size={14} />
            <span className="text-xs klareet-mono" data-testid="ground-label">{currentGround.label}</span>
          </button>

          {authed && <AccountMenu />}
        </div>
      </div>
    </header>
  );
}
